import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NbDialogService, NbLayoutScrollService } from "@nebular/theme";
import { replace, remove, clone, find } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { User } from "../../@core/data/users";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";
import Comment from "../../models/comment.model";
import { WhereList } from "../../models/pagination";
import WorkingTask from "../../models/working-task.model";
import { CommentService } from "../../services/comment.service";
import { DialogYesNoPromptComponent } from "../dialog-yesno-prompt/dialog-yesno-prompt.component";

@Component({
  selector: "ngx-view-comments",
  styleUrls: ["./view-comments.component.scss"],
  templateUrl: "./view-comments.component.html",
})
export class ViewCommentsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();

  @Input() workingTask? = new WorkingTask();

  @Input() mode?: string = ""; //course - other - student
  @Output() onClose: EventEmitter<any> = new EventEmitter();

  refTitle: string = "";
  refUid: string = "";
  newComment = true;

  replyForm: FormGroup;
  submitted = false;
  loading = false;

  comments?: Comment[] = [];
  reply?: Comment = new Comment();
  wheres: WhereList[] = [];

  isLoggedIn = false;
  uid = "anonymous";
  pageSize = 10;
  cursor: any;
  done = false;

  posX = 0;
  posY = 0;

  constructor(
    private fb: FormBuilder,
    private commentService: CommentService,
    public userService: UserService,
    private dialogService: NbDialogService,
    private scrollService: NbLayoutScrollService
  ) {}

  ngOnInit() {
    this.replyForm = this.fb.group({
      content: Validators.required,
    });

    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
          this.isLoggedIn = true;
        } else {
          this.uid = "";
        }
      });

    this.scrollService
      .getPosition()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.posX = data.x;
        this.posY = data.y;
      });

    this.initComment();
    this.getComments();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMore() {
    this.pageSize = 12;
    this.getComments();
  }

  createReply() {
    this.loading = true;
    this.submitted = true;

    this.reply.content = this.reply.content.trim();
    this.commentService
      .create(this.reply)
      .then((value) => {
        this.calculateNoComment(1);

        const newReply = clone(value);
        this.comments = [newReply, ...this.comments];

        this.initComment();
        this.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
      });
  }

  deleteReply(item) {
    this.dialogService
      .open(DialogYesNoPromptComponent, {
        context: {
          title: "Bạn có chắc xóa nội dung này?",
        },
      })
      .onClose.subscribe((value) => {
        if (value) {
          this.submitted = true;
          this.loading = true;
          this.submitted = true;

          this.commentService
            .delete(item.id)
            .then((result) => {
              this.calculateNoComment(-1);

              remove(this.comments, (e) => {
                return e.id == item.id;
              });
              this.loading = false;
              this.submitted = false;
            })
            .catch((err) => {
              this.loading = false;
              this.submitted = false;
            });
        }
      });
  }

  //Editing
  content = "";
  onEditReply(item: Comment) {
    item.editing = true;
    this.content = item.content;
  }

  editReply(item: Comment) {
    item.loading = true;
    this.submitted = true;
    item.content = this.content;

    this.commentService
      .update(item)
      .then((data) => {
        item.editing = false;

        item.loading = false;
        this.submitted = false;
      })
      .catch((err) => {
        item.editing = false;
        item.loading = false;
        this.submitted = false;
      });
  }

  cancelReply(item: Comment) {
    item.editing = false;
  }

  onNewReply(item: Comment) {
    if (item) {
      this.reply.toId = item.id;
      this.reply.toUid = item.createdBy;
      this.reply.toAuthor = item.author;
      this.reply.refTitle = item.content;
      this.reply.level = (item.level ? item.level : 0) + 1;
    } else {
      this.initComment();
    }

    this.scrollService.scrollTo(this.posX, this.posY);
  }

  closeReplies() {
    this.onClose.emit(true);
  }

  readMoreRefTitle(item) {
    item.moreRefTitle = item.moreRefTitle == true ? false : true;
  }

  private initComment() {
    this.reply = new Comment();
    this.reply.level = 0;
    this.reply.mode = "";
    this.reply.refId = "";

    if (this.workingTask && this.workingTask.id) {
      this.reply.mode = "working-tasks";
      this.reply.refId = this.workingTask.id;
      this.reply.refTitle = this.workingTask.title;
      this.reply.refUid = this.workingTask.createdBy;
      this.reply.refAuthor = this.workingTask.author;

      this.reply.privacy = this.workingTask.privacy;
      this.reply.toUid = this.workingTask.createdBy;
      this.reply.toAuthor = this.workingTask.author;
      this.reply.refUrl = `/working-tasks/${this.workingTask.id}`;

      this.refUid = this.workingTask.createdBy;
      this.refTitle = this.workingTask.title;
      this.newComment = true;
    }

    console.log("ViewCommentsComponent/initComment:", this.reply);
  }

  private getComments(): void {
    if (this.reply.mode != "" && this.reply.refId != "") {
      this.loading = true;
      this.wheres = [];
      this.wheres.push({
        fieldPath: "mode",
        value: this.reply.mode,
      });
      this.wheres.push({
        fieldPath: "refId",
        value: this.reply.refId,
      });

      console.log("ViewCommentsComponent/getComments/wheres:", this.wheres);

      this.commentService
        .getComments(this.wheres, this.pageSize, this.cursor)
        .then((querySnapshot) => {
          console.log("ViewCommentsComponent/getComments:", querySnapshot.docs);
          if (querySnapshot.docs.length > 0) {
            this.cursor = querySnapshot.docs[querySnapshot.docs.length - 1];

            querySnapshot.forEach((doc) => {
              let data = new Comment();
              data = doc.data();
              data.id = doc.id;
              data.createdOn = convertTimestamp(doc.data().createdOn);
              data.level = data.level ? data.level : 0;
              data.moreRefTitle = false;

              let item = find(this.comments, ["id", data.id]);
              if (item) {
                item = data;
              } else {
                this.comments.push(data);
              }
            });
          } else {
            this.cursor = null;
            this.done = true;
          }

          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
        });
    }
  }

  private calculateNoComment(value) {
    if (this.workingTask && this.workingTask.id) {
      this.workingTask.noComment += value;
    }
  }
}
